import React, { useMemo, useState } from 'react'
import {
  Box,
  VStack,
  FormControl,
  FormLabel,
  FormHelperText,
  FormErrorMessage,
  Textarea,
  Button,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Text,
  useColorModeValue,
  useDisclosure,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Badge,
  HStack,
  Link,
  Code,
  Divider,
  Collapse,
  useDisclosure as useCollapseDisclosure,
} from '@chakra-ui/react'
import { FiUploadCloud, FiExternalLink, FiChevronDown, FiChevronRight } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { getExplorerAddressUrl, getExplorerTxUrl } from '@/constants/chains'
import {
  contractService,
  type LifecycleBatchTriggerResult,
} from '@/services/admin/contractService'
import { LifecycleBatchItemResults } from './LifecycleBatchItemResults'
import { DropdownSelect } from '@/components/common/DropdownSelect'
import {
  getTriggerProgressStatus,
  ProgressModal,
  type ProgressStatus,
} from '@/components/common/ProgressModal'
import {
  findEntriesWithoutPrefix,
  parseMultilineEntries,
} from '@/components/common/triggerInputHelpers'
import { useApiError } from '@/hooks/useApiError'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { useToast } from '@/hooks/useToast'
import { buildContractChainOptions } from './contractChainOptions'

// Enhanced error details interface
interface DeploymentErrorDetails {
  transaction_hash?: string
  factory_address?: string
  chain_id?: number
  target_address?: string
  gas_used?: string
  gas_price?: string
  error_code?: string
  block_number?: number
}

// Enhanced deployment result interface
interface EnhancedDeploymentResult extends LifecycleBatchTriggerResult {
  details?: {
    successful_deployments?: Array<{
      registry_id: string
      transaction_hash: string
      factory_address: string
      chain_id: number
      gas_used?: string
      deployment_cost_usd?: string
    }>
    failed_deployments?: Array<{
      registry_id: string
      error: string
      details?: DeploymentErrorDetails
    }>
    factory_addresses?: { [chainId: string]: string }
  }
}

export const ContractDeploymentTrigger: React.FC = () => {
  const { t } = useTranslation()
  const toast = useToast()
  const { handleError } = useApiError()
  const { currentEnv } = useNetworkEnvironment()
  const bgColor = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const { isOpen: isProgressOpen, onOpen: onProgressOpen, onClose: onProgressClose } = useDisclosure()

  const [orderIds, setOrderIds] = useState('')
  const [chainId, setChainId] = useState('')
  const [limit, setLimit] = useState(10)
  const [orderIdsError, setOrderIdsError] = useState('')
  const [results, setResults] = useState<EnhancedDeploymentResult | null>(null)
  const { isOpen: isDetailsOpen, onToggle: onToggleDetails } = useCollapseDisclosure()
  const [progressStatus, setProgressStatus] = useState<ProgressStatus>('idle')
  const [progressCurrent, setProgressCurrent] = useState(0)
  const [progressTotal, setProgressTotal] = useState(0)
  const [progressMessage, setProgressMessage] = useState('')
  const chainOptions = useMemo(() => buildContractChainOptions(currentEnv), [currentEnv])
  const chainOptionMap = useMemo(
    () => new Map(chainOptions.map(option => [option.value, option])),
    [chainOptions]
  )
  const chainLabelByNumericId = useMemo(
    () => new Map(chainOptions.map(option => [option.chainId, option.label])),
    [chainOptions]
  )
  const selectedChainId = chainId
    ? chainOptionMap.get(chainId)?.chainId ?? 'unknown'
    : null

  const executeDeployment = async () => {
    const orderIdList = parseMultilineEntries(orderIds)

    const invalidIds = findEntriesWithoutPrefix(orderIdList, 'ord_')
    if (invalidIds.length > 0) {
      throw new Error(`Invalid order IDs: ${invalidIds.join(', ')}`)
    }

    const params: {
      limit: number
      orderIds?: string[]
      chainId?: string
    } = { limit }

    if (orderIdList.length > 0) {
      params.orderIds = orderIdList
    }

    if (chainId) {
      params.chainId = chainId
    }

    setProgressTotal(limit)
    setProgressCurrent(0)
    setProgressMessage(t('monitoring.contractDeployment.progress.preparing'))
    setProgressStatus('processing')
    onProgressOpen()

    return await contractService.triggerContractDeployment(params, currentEnv)
  }

  const mutation = useMutation({
    mutationFn: executeDeployment,
    onSuccess: (response) => {
      if (response) {
        setResults(response)
        const { queued, alreadyQueued, skipped, failed, total } = response
        if (import.meta.env.DEV) {
          console.info('[contract-deployment] trigger result', {
            total,
            queued,
            alreadyQueued,
            skipped,
            failed,
          })
        }
        setProgressTotal(total)
        setProgressCurrent(total)
        setProgressStatus(getTriggerProgressStatus(failed))
        setProgressMessage(t('monitoring.contractDeployment.progress.completed'))
        
        // Add to operation history if available
        if (typeof window !== 'undefined' && (window as any).addOperationHistory) {
          (window as any).addOperationHistory({
            type: 'contractDeployment',
            operator: 'Admin',
            result: failed > 0 ? 'partial' : 'success',
            details: `Queued: ${queued}, Already Queued: ${alreadyQueued}, Skipped: ${skipped}, Failed: ${failed}`,
            metadata: { queued, alreadyQueued, skipped, failed, factoryAddresses: (response as any).details?.factory_addresses }
          })
        }
        
        const toastMethod = failed > 0
          ? toast.warning
          : queued > 0
          ? toast.success
          : toast.info
        const factoryInfo = (response as any).details?.factory_addresses 
          ? ` (Factory addresses: ${Object.keys((response as any).details.factory_addresses).length} chains)`
          : ''
        
        toastMethod({
          title: t('monitoring.contractDeployment.success.title'),
          description: t('monitoring.contractDeployment.success.description', {
            queued: queued ?? 0,
            alreadyQueued: alreadyQueued ?? 0,
            skipped: skipped ?? 0,
            failed: failed ?? 0
          }) + factoryInfo,
          duration: 5000,
          isClosable: true,
          id: 'contract-deployment-success', // Unique ID to prevent duplicates
        })
      }
    },
    onError: (error: any) => {
      setProgressStatus('failed')
      const errorMessage = error.response?.data?.error || error.message || 'Failed to trigger contract deployment'
      const errorDetails = error.response?.data?.details || null
      
      // Enhanced error message with transaction details if available
      let enhancedMessage = errorMessage
      if (errorDetails?.transaction_hash) {
        const explorerUrl = getExplorerTxUrl(
          errorDetails.chain_id,
          errorDetails.transaction_hash
        )
        enhancedMessage += explorerUrl ? ` (Tx: ${errorDetails.transaction_hash})` : ''
      }
      
      setProgressMessage(enhancedMessage)
      
      // Add to operation history if available
      if (typeof window !== 'undefined' && (window as any).addOperationHistory) {
        (window as any).addOperationHistory({
          type: 'contractDeployment',
          operator: 'Admin',
          result: 'failed',
          details: errorMessage,
          errorCode: error.response?.status,
          metadata: errorDetails
        })
      }
      
      // Use the common error handler
      handleError(error, 'Contract deployment')
      
      if (process.env.NODE_ENV === 'development') {
        console.error('Contract Deployment Error Details:', {
          status: error.response?.status,
          data: error.response?.data,
          errorDetails,
          headers: error.response?.headers
        })
      }
    },
  })

  const handleOrderIdsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value
    setOrderIds(value)
    
    if (value) {
      const orderIdList = parseMultilineEntries(value)
      const invalidIds = findEntriesWithoutPrefix(orderIdList, 'ord_')
      if (invalidIds.length > 0) {
        setOrderIdsError(`Invalid order IDs: ${invalidIds.slice(0, 3).join(', ')}`)
      } else {
        setOrderIdsError('')
      }
    } else {
      setOrderIdsError('')
    }
  }

  const handleSubmit = () => {
    if (orderIdsError) {
      return
    }
    
    mutation.mutate()
  }

  const getStatColor = (value: number, type: 'queued' | 'alreadyQueued' | 'skipped' | 'failed') => {
    if (value === 0) return 'gray'
    switch (type) {
      case 'queued':
        return 'green'
      case 'alreadyQueued':
        return 'blue'
      case 'skipped':
        return 'yellow'
      case 'failed':
        return 'red'
      default:
        return 'gray'
    }
  }

  return (
    <VStack spacing={6} align="stretch">
      <Alert status="info" borderRadius="md">
        <AlertIcon />
        {t('monitoring.contractDeployment.description')}
      </Alert>

      <Box>
        <VStack spacing={4} align="stretch">
          <FormControl isInvalid={!!orderIdsError}>
            <FormLabel>{t('monitoring.contractDeployment.fields.orderIds.label')}</FormLabel>
            <Textarea
              placeholder={t('monitoring.contractDeployment.fields.orderIds.placeholder')}
              value={orderIds}
              onChange={handleOrderIdsChange}
              rows={4}
              isDisabled={mutation.isPending}
            />
            {!orderIdsError && (
              <FormHelperText>
                {t('monitoring.contractDeployment.fields.orderIds.helper')}
              </FormHelperText>
            )}
            <FormErrorMessage>{orderIdsError}</FormErrorMessage>
          </FormControl>

          <FormControl>
            <FormLabel>{t('monitoring.contractDeployment.fields.chainId.label')}</FormLabel>
            <DropdownSelect
              placeholder={t('monitoring.contractDeployment.fields.chainId.placeholder')}
              emptyOptionLabel={t('monitoring.contractDeployment.fields.chainId.placeholder')}
              value={chainId}
              options={chainOptions}
              onChange={setChainId}
              ariaLabel={t('monitoring.contractDeployment.fields.chainId.label')}
              isDisabled={mutation.isPending}
            />
            <FormHelperText>
              {t('monitoring.contractDeployment.fields.chainId.helper')}
            </FormHelperText>
          </FormControl>

          <FormControl>
            <FormLabel>{t('monitoring.contractDeployment.fields.limit.label')}</FormLabel>
            <NumberInput
              value={limit}
              onChange={(_, value) => setLimit(value || 10)}
              min={1}
              max={100}
              isDisabled={mutation.isPending}
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
            <FormHelperText>
              {t('monitoring.contractDeployment.fields.limit.helper')}
            </FormHelperText>
          </FormControl>

          <Button
            colorScheme="blue"
            leftIcon={<FiUploadCloud />}
            onClick={handleSubmit}
            isLoading={mutation.isPending}
            loadingText={t('monitoring.contractDeployment.button.loading')}
            isDisabled={mutation.isPending || !!orderIdsError}
          >
            {t('monitoring.contractDeployment.button.submit')}
          </Button>
          
          {mutation.isPending && (
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <Box>
                <AlertTitle>Processing Deployment</AlertTitle>
                <AlertDescription>
                  <Text fontSize="sm">
                    This operation may take up to 45 seconds depending on gas prices and blockchain congestion.
                  </Text>
                  <Text fontSize="sm" mt={1}>
                    Each contract deployment requires blockchain confirmation.
                  </Text>
                </AlertDescription>
              </Box>
            </Alert>
          )}
        </VStack>
      </Box>

      {results && (
        <Box
          bg={bgColor}
          p={6}
          borderRadius="md"
          borderWidth={1}
          borderColor={borderColor}
        >
          <VStack spacing={4} align="stretch">
            <Box fontWeight="semibold" fontSize="lg">
              {t('monitoring.contractDeployment.results.title')}
            </Box>
            <SimpleGrid columns={{ base: 2, md: 5 }} spacing={4}>
              <Stat>
                <StatLabel>
                  {t('monitoring.contractDeployment.results.total')}
                </StatLabel>
                <StatNumber>{results.total}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel>
                  <HStack spacing={1}>
                    <span>{t('monitoring.contractDeployment.results.queued')}</span>
                    <Badge colorScheme={getStatColor(results.queued, 'queued')} size="sm">
                      {results.queued > 0 ? 'Queued' : '0'}
                    </Badge>
                  </HStack>
                </StatLabel>
                <StatNumber color={`${getStatColor(results.queued, 'queued')}.500`}>
                  {results.queued}
                </StatNumber>
                {results.total > 0 && (
                  <StatHelpText>
                    {((results.queued / results.total) * 100).toFixed(0)}%
                  </StatHelpText>
                )}
              </Stat>
              <Stat>
                <StatLabel>
                  <HStack spacing={1}>
                    <span>{t('monitoring.contractDeployment.results.alreadyQueued')}</span>
                    <Badge colorScheme={getStatColor(results.alreadyQueued, 'alreadyQueued')} size="sm">
                      {results.alreadyQueued > 0 ? 'Already In Queue' : '0'}
                    </Badge>
                  </HStack>
                </StatLabel>
                <StatNumber color={`${getStatColor(results.alreadyQueued, 'alreadyQueued')}.500`}>
                  {results.alreadyQueued}
                </StatNumber>
                {results.total > 0 && (
                  <StatHelpText>
                    {((results.alreadyQueued / results.total) * 100).toFixed(0)}%
                  </StatHelpText>
                )}
              </Stat>
              <Stat>
                <StatLabel>
                  <HStack spacing={1}>
                    <span>{t('monitoring.contractDeployment.results.skipped')}</span>
                    <Badge colorScheme={getStatColor(results.skipped, 'skipped')} size="sm">
                      {results.skipped > 0 ? 'Skipped' : '0'}
                    </Badge>
                  </HStack>
                </StatLabel>
                <StatNumber color={`${getStatColor(results.skipped, 'skipped')}.500`}>
                  {results.skipped}
                </StatNumber>
                {results.total > 0 && (
                  <StatHelpText>
                    {((results.skipped / results.total) * 100).toFixed(0)}%
                  </StatHelpText>
                )}
              </Stat>
              <Stat>
                <StatLabel>
                  <HStack spacing={1}>
                    <span>{t('monitoring.contractDeployment.results.failed')}</span>
                    <Badge colorScheme={getStatColor(results.failed, 'failed')} size="sm">
                      {results.failed > 0 ? 'Failed' : '0'}
                    </Badge>
                  </HStack>
                </StatLabel>
                <StatNumber color={`${getStatColor(results.failed, 'failed')}.500`}>
                  {results.failed}
                </StatNumber>
                {results.total > 0 && (
                  <StatHelpText>
                    {((results.failed / results.total) * 100).toFixed(0)}%
                  </StatHelpText>
                )}
              </Stat>
            </SimpleGrid>

            <LifecycleBatchItemResults items={results.items} />
            
            {/* Factory Addresses Summary */}
            {results.details?.factory_addresses && Object.keys(results.details.factory_addresses).length > 0 && (
              <>
                <Divider />
                <Box>
                  <Text fontWeight="medium" mb={2}>Factory Addresses Used:</Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                    {Object.entries(results.details.factory_addresses).map(([chainId, address]) => {
                      const numericChainId = Number.parseInt(chainId, 10)
                      const chainLabel = chainLabelByNumericId.get(numericChainId)
                      const explorerUrl = getExplorerAddressUrl(chainId, address)
                      return (
                        <HStack key={chainId} spacing={2} fontSize="sm">
                          <Badge variant="outline">{chainLabel || `Chain ${chainId}`}</Badge>
                          {explorerUrl ? (
                            <Link href={explorerUrl} isExternal color="blue.500">
                              <Code fontSize="xs">{address}</Code>
                              <FiExternalLink style={{ display: 'inline', marginLeft: '4px' }} />
                            </Link>
                          ) : (
                            <Code fontSize="xs">{address}</Code>
                          )}
                        </HStack>
                      )
                    })}
                  </SimpleGrid>
                </Box>
              </>
            )}
            
            {/* Detailed Results Toggle */}
            {((results as any).details?.successful_deployments?.length > 0 || (results as any).details?.failed_deployments?.length > 0) && (
              <>
                <Divider />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onToggleDetails}
                  leftIcon={isDetailsOpen ? <FiChevronDown /> : <FiChevronRight />}
                >
                  {isDetailsOpen ? 'Hide' : 'Show'} Deployment Details
                </Button>
                
                <Collapse in={isDetailsOpen}>
                  <VStack spacing={4} align="stretch">
                    
                    {/* Successful Deployments */}
                    {results.details?.successful_deployments && results.details.successful_deployments.length > 0 && (
                      <Box>
                        <Text fontWeight="medium" color="green.500" mb={2}>
                          Successful Deployments ({results.details.successful_deployments.length})
                        </Text>
                        <VStack spacing={2} align="stretch">
                          {results.details.successful_deployments.map((deployment, index) => {
                            const explorerUrl = getExplorerTxUrl(
                              deployment.chain_id,
                              deployment.transaction_hash
                            )
                            const chainLabel = chainLabelByNumericId.get(deployment.chain_id)
                            return (
                              <Box key={index} p={3} borderWidth={1} borderColor="green.200" borderRadius="md" bg="green.50">
                                <HStack justify="space-between" wrap="wrap">
                                  <VStack align="start" spacing={1} fontSize="sm">
                                    <HStack>
                                      <Badge variant="outline">{chainLabel || `Chain ${deployment.chain_id}`}</Badge>
                                      <Code fontSize="xs">{deployment.registry_id}</Code>
                                    </HStack>
                                    {deployment.deployment_cost_usd && (
                                      <Text color="gray.600">
                                        Cost: ${deployment.deployment_cost_usd}
                                        {deployment.gas_used && ` (${deployment.gas_used} gas)`}
                                      </Text>
                                    )}
                                  </VStack>
                                  {explorerUrl && (
                                    <Link href={explorerUrl} isExternal color="blue.500" fontSize="sm">
                                      View Transaction <FiExternalLink style={{ display: 'inline' }} />
                                    </Link>
                                  )}
                                </HStack>
                              </Box>
                            )
                          })}
                        </VStack>
                      </Box>
                    )}
                    
                    {/* Failed Deployments */}
                    {results.details?.failed_deployments && results.details.failed_deployments.length > 0 && (
                      <Box>
                        <Text fontWeight="medium" color="red.500" mb={2}>
                          Failed Deployments ({results.details.failed_deployments.length})
                        </Text>
                        <VStack spacing={2} align="stretch">
                          {results.details.failed_deployments.map((failure, index) => {
                            const explorerUrl = failure.details?.transaction_hash 
                              ? getExplorerTxUrl(
                                  failure.details.chain_id,
                                  failure.details.transaction_hash
                                )
                              : null
                            const chainLabel = failure.details?.chain_id
                              ? chainLabelByNumericId.get(failure.details.chain_id)
                              : null
                              
                            return (
                              <Box key={index} p={3} borderWidth={1} borderColor="red.200" borderRadius="md" bg="red.50">
                                <VStack align="stretch" spacing={2} fontSize="sm">
                                  <HStack>
                                    {chainLabel && (
                                      <Badge variant="outline">{chainLabel}</Badge>
                                    )}
                                    <Code fontSize="xs">{failure.registry_id}</Code>
                                  </HStack>
                                  <Text color="red.600" fontWeight="medium">
                                    {failure.error}
                                  </Text>
                                  {failure.details && (
                                    <VStack align="start" spacing={1} fontSize="xs" color="gray.600">
                                      {failure.details.factory_address && (
                                        <Text>Factory: <Code>{failure.details.factory_address}</Code></Text>
                                      )}
                                      {failure.details.target_address && (
                                        <Text>Target: <Code>{failure.details.target_address}</Code></Text>
                                      )}
                                      {failure.details.gas_used && (
                                        <Text>Gas Used: {failure.details.gas_used}</Text>
                                      )}
                                      {failure.details.error_code && (
                                        <Text>Error Code: {failure.details.error_code}</Text>
                                      )}
                                    </VStack>
                                  )}
                                  {explorerUrl && (
                                    <Link href={explorerUrl} isExternal color="blue.500" fontSize="sm">
                                      View Failed Transaction <FiExternalLink style={{ display: 'inline' }} />
                                    </Link>
                                  )}
                                </VStack>
                              </Box>
                            )
                          })}
                        </VStack>
                      </Box>
                    )}
                  </VStack>
                </Collapse>
              </>
            )}
            
            {process.env.NODE_ENV === 'development' && (
              <>
                <Divider />
                <Text fontSize="sm" color="gray.500">
                  Debug: {selectedChainId && `Chain: ${selectedChainId}`} |
                  {orderIds
                    ? `${orderIds.split('\n').filter(id => id.trim()).length} specific IDs`
                    : 'All orders'} |
                  Limit: {limit}
                </Text>
              </>
            )}
          </VStack>
        </Box>
      )}

      {/* Progress Modal */}
      <ProgressModal
        isOpen={isProgressOpen}
        onClose={onProgressClose}
        title={t('monitoring.contractDeployment.progress.title')}
        total={progressTotal}
        current={progressCurrent}
        status={progressStatus}
        message={progressMessage}
        results={results ? {
          queued: results.queued,
          alreadyQueued: results.alreadyQueued,
          failed: results.failed,
          skipped: results.skipped
        } : undefined}
      />
    </VStack>
  )
}
